import { Button, Checkbox, InlineSelect } from "@qualtio-workhub/ui";
import { type ReactNode, useEffect, useId, useRef, useState } from "react";
import { useI18n } from "../../shared/i18n/provider";

const unassignedValue = "__workhub_unassigned__";
type Option = { id: string; name: string };
type ReferenceState = { loading: boolean; error: string | null; refetch: () => Promise<void> };

export function IssuePropertySelect({
  field,
  label,
  value,
  displayValue,
  options,
  editable,
  disabled,
  reference,
  onSave,
}: {
  field: string;
  label: string;
  value: string;
  displayValue?: ReactNode;
  options: readonly Option[];
  editable: boolean;
  disabled: boolean;
  reference?: ReferenceState;
  onSave: (value: string | null) => Promise<void>;
}) {
  const { t } = useI18n();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const [lastAttempt, setLastAttempt] = useState<string | null>(null);
  const lock = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);
  const currentLabel =
    options.find((option) => option.id === value)?.name ??
    (value ? t("issue.inline.unavailable") : t("issue.unassigned"));
  const items = [
    ...(reference ? [{ value: unassignedValue, label: t("issue.unassigned") }] : []),
    ...options.map((option) => ({ value: option.id, label: option.name })),
    ...(value && !options.some((option) => option.id === value)
      ? [{ value, label: currentLabel }]
      : []),
  ];

  useEffect(() => {
    if (!saving && !disabled && returnFocus.current) {
      trigger.current?.focus({ preventScroll: true });
      returnFocus.current = false;
    }
  }, [saving, disabled]);

  async function save(next: string | null) {
    if (lock.current || disabled || next === (value || null)) return;
    lock.current = true;
    returnFocus.current = true;
    setLastAttempt(next);
    setSaving(true);
    setError(false);
    try {
      await onSave(next);
    } catch {
      setError(true);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="issue-property-edit" aria-busy={saving}>
      <span className="issue-property-label">{label}</span>
      <div className="issue-property-value">
        {editable ? (
          <InlineSelect
            disabled={disabled || saving || reference?.loading || Boolean(reference?.error)}
            id={`detail-${field}`}
            label={label}
            options={items}
            ref={trigger}
            value={value || unassignedValue}
            onValueChange={(next) => void save(next === unassignedValue ? null : next)}
          >
            {displayValue ?? currentLabel}
          </InlineSelect>
        ) : (
          <strong>{displayValue ?? currentLabel}</strong>
        )}
      </div>
      {saving ? (
        <small className="issue-property-feedback muted-copy" role="status">
          {t("common.saving")}
        </small>
      ) : null}
      {error || (editable && reference?.error) ? (
        <div className="issue-property-feedback">
          <p className="error-text" role="alert">
            {t(error ? "issue.edit.error" : "issue.inline.optionsError")}
          </p>
          <Button
            disabled={disabled || saving}
            onClick={() => (error ? void save(lastAttempt) : void reference?.refetch())}
            size="sm"
            variant="ghost"
          >
            {t("common.retry")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function IssueLabelEditor({
  value,
  labels,
  editable,
  disabled,
  reference,
  onSave,
}: {
  value: string[];
  labels: readonly Option[];
  editable: boolean;
  disabled: boolean;
  reference: ReferenceState;
  onSave: (ids: string[]) => Promise<void>;
}) {
  const { t } = useI18n();
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);
  const lock = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const returnFocus = useRef(false);
  const labelNames =
    value
      .map(
        (labelId) =>
          labels.find((label) => label.id === labelId)?.name ?? t("issue.inline.unavailable"),
      )
      .join(", ") || t("issue.labels.none");

  useEffect(() => {
    if (!editing && returnFocus.current) {
      trigger.current?.focus({ preventScroll: true });
      returnFocus.current = false;
    }
  }, [editing]);

  function close() {
    returnFocus.current = true;
    setEditing(false);
    setError(false);
  }

  async function save() {
    if (lock.current || disabled || reference.error || reference.loading) return;
    if (draft.length === value.length && draft.every((labelId) => value.includes(labelId))) {
      close();
      return;
    }
    lock.current = true;
    setSaving(true);
    setError(false);
    try {
      await onSave(draft);
      close();
    } catch {
      setError(true);
    } finally {
      lock.current = false;
      setSaving(false);
    }
  }

  return (
    <div className="issue-property-edit">
      <span className="issue-property-label">{t("issue.field.labels")}</span>
      {!editing ? (
        <div className="issue-property-value">
          {editable ? (
            <Button
              aria-label={`${t("issue.inline.edit")} ${t("issue.field.labels").toLocaleLowerCase()}`}
              disabled={disabled || reference.loading}
              onClick={() => {
                setDraft(value);
                setError(false);
                setEditing(true);
              }}
              ref={trigger}
              size="sm"
              variant="ghost"
            >
              <span className="issue-property-value-text">{labelNames}</span>
            </Button>
          ) : (
            <strong>{labelNames}</strong>
          )}
        </div>
      ) : null}
      {editing ? (
        <form
          aria-label={t("issue.field.labels")}
          className="issue-inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape" && !saving) {
              event.preventDefault();
              event.stopPropagation();
              close();
            }
          }}
        >
          <fieldset
            className="issue-label-options"
            disabled={saving || disabled || reference.loading || Boolean(reference.error)}
          >
            <legend className="sr-only">{t("issue.field.labels")}</legend>
            {labels.length === 0 && !reference.loading && !reference.error ? (
              <p className="muted-copy">{t("issue.labels.none")}</p>
            ) : null}
            {labels.map((label, index) => (
              <div className="checkbox-row" key={label.id}>
                <Checkbox
                  autoFocus={index === 0}
                  checked={draft.includes(label.id)}
                  id={`${id}-${label.id}`}
                  onCheckedChange={(checked) =>
                    setDraft((current) =>
                      checked === true
                        ? [...current, label.id]
                        : current.filter((labelId) => labelId !== label.id),
                    )
                  }
                />
                <label htmlFor={`${id}-${label.id}`}>{label.name}</label>
              </div>
            ))}
          </fieldset>
          {error || reference.error ? (
            <p className="error-text" role="alert">
              {t(error ? "issue.edit.error" : "issue.inline.optionsError")}
            </p>
          ) : null}
          <div className="issue-inline-actions">
            {reference.error ? (
              <Button
                disabled={reference.loading}
                onClick={() => void reference.refetch()}
                size="sm"
                variant="secondary"
              >
                {t("common.retry")}
              </Button>
            ) : (
              <Button disabled={saving || disabled || reference.loading} size="sm" type="submit">
                {t(saving ? "common.saving" : "common.save")}
              </Button>
            )}
            <Button disabled={saving} onClick={close} size="sm" variant="ghost">
              {t("common.cancel")}
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
